import MailIcon from '@mui/icons-material/Mail';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import SmsIcon from '@mui/icons-material/Sms';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import { CognitoUser } from 'amazon-cognito-identity-js';
import React from 'react';
import { MfaMethod, MfaStatus, useMfaStatus } from '../hooks/use-mfa-status';
import { MfaSetupDrawer } from '../routes/account/mfa-setup-drawer';
import { pageSurface } from './alliance/alliance-surface';
import { SectionErrorScreen } from './alliance/system-screens';

/** The three methods, in the order the design lists them */
const METHODS: {
  method: MfaMethod;
  icon: React.ReactNode;
  name: string;
  description: string;
}[] = [
  {
    method: 'SOFTWARE_TOKEN_MFA',
    icon: <SmartphoneIcon />,
    name: 'Authenticator app',
    description: 'Codes from Google or Microsoft Authenticator',
  },
  {
    method: 'SMS_MFA',
    icon: <SmsIcon />,
    name: 'Text message',
    description: 'A code sent to your mobile number',
  },
  {
    method: 'EMAIL_OTP',
    icon: <MailIcon />,
    name: 'Email',
    description: 'A code sent to your email address',
  },
];

/** Preferred (used at sign-in), On (enabled, not preferred) or Not set up */
const getMethodState = (params: {
  status: MfaStatus | undefined;
  method: MfaMethod;
}) => {
  if (params.status?.preferred === params.method) {
    return 'Preferred';
  }
  if (params.status?.enabled.includes(params.method)) {
    return 'On';
  }
  return 'Not set up';
};

/**
 * "Verification methods" on the MFA System Screen (mfaEmbed): the three
 * methods with their status, so people know what to expect, and a Set up
 * button that opens the same drawer the Agent Profile uses. Clicking a
 * method opens the drawer on that method.
 *
 * @param user - The signed-in Cognito user
 */
export function MfaMethodsCard(props: { user: CognitoUser }) {
  // State
  const [open, setOpen] = React.useState(false);
  const [initialMethod, setInitialMethod] = React.useState<MfaMethod>();
  // Hooks
  const query = useMfaStatus();

  const c = pageSurface();
  const ready = query.isSuccess;

  /** Open the drawer, optionally on one method */
  const openDrawer = (method?: MfaMethod) => {
    setInitialMethod(method);
    setOpen(true);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Box
        sx={{
          border: `1px solid ${c.border}`,
          backgroundColor: c.paper,
          borderRadius: '14px',
          overflow: 'hidden',
          color: c.text,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            p: '14px 16px',
            borderBottom: `1px solid ${c.border}`,
          }}
        >
          <Box
            component="h2"
            sx={{ flex: 1, m: 0, fontSize: 15, fontWeight: 500 }}
          >
            Verification methods
          </Box>
          <ButtonBase
            disabled={!ready}
            onClick={() => openDrawer()}
            sx={{
              height: 36,
              px: '16px',
              borderRadius: '10px',
              backgroundColor: c.primary.bg,
              color: c.primary.fg,
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 500,
              '&:hover': { filter: 'brightness(0.95)' },
              '&.Mui-disabled': { opacity: 0.5 },
              '&.Mui-focusVisible': {
                outline: `2px solid ${c.primary.bg}`,
                outlineOffset: 2,
              },
            }}
          >
            Set up
          </ButtonBase>
        </Box>

        {query.isError ? (
          <SectionErrorScreen
            onRetry={() => query.refetch()}
            message={(query.error as { message?: string } | null)?.message}
            dense
          />
        ) : (
          METHODS.map((item, index) => {
            const state = getMethodState({
              status: query.data,
              method: item.method,
            });
            return (
              <ButtonBase
                key={item.method}
                disabled={!ready}
                onClick={() => openDrawer(item.method)}
                aria-label={`${item.name}: ${state}. Set up ${item.name}`}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  p: '12px 16px',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  borderTop: index === 0 ? 'none' : `1px solid ${c.border}`,
                  '&:hover': { backgroundColor: c.hover },
                  '&.Mui-focusVisible': { backgroundColor: c.hover },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    color: c.text2,
                    '& svg': { fontSize: 22 },
                  }}
                >
                  {item.icon}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  <Box sx={{ fontSize: 14, fontWeight: 500 }}>{item.name}</Box>
                  <Box sx={{ fontSize: 13, color: c.text2 }}>
                    {item.description}
                  </Box>
                </Box>
                {ready ? (
                  <Box
                    sx={{
                      flex: 'none',
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: 1.4,
                      borderRadius: '8px',
                      p: '3px 8px',
                      border: `1px solid ${
                        state === 'Preferred' ? c.link : c.border
                      }`,
                      color:
                        state === 'Preferred'
                          ? c.link
                          : state === 'On'
                            ? c.text
                            : c.text2,
                    }}
                  >
                    {state}
                  </Box>
                ) : (
                  <Skeleton
                    variant="rounded"
                    width={72}
                    height={24}
                    sx={{ borderRadius: '8px' }}
                  />
                )}
              </ButtonBase>
            );
          })
        )}
      </Box>

      <Box sx={{ fontSize: 14, lineHeight: 1.5, color: c.text2 }}>
        You can change this any time on the{' '}
        <Box component="span" sx={{ fontWeight: 500, color: c.text }}>
          Agent Profile
        </Box>{' '}
        page.
      </Box>

      <MfaSetupDrawer
        user={props.user}
        open={open}
        onClose={() => setOpen(false)}
        initialMethod={initialMethod}
      />
    </Box>
  );
}
